import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalInterceptorAuthRequest, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, SilentRequest } from '@azure/msal-browser';
import { authentication } from '@microsoft/teams-js';
import { first } from 'lodash-es';
import moment, { Moment } from 'moment';
import { EMPTY, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, map, mapTo, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { RecurringTaskRunnerFactory } from '@nimbus-library/features/recurring-task';
import { LogoutRequest } from '@nimbus-library/features/authentication/models/authentication-request.model';
import { Prompt } from '@nimbus-library/features/authentication/models/authentication.enum';
import { MicrosoftResource } from '@nimbus-library/features/authentication/models/graph-permission.enum';
import { IAuthenticationStorage } from '@nimbus-library/features/authentication/services/i-authentication-storage';
import { IApiToken, IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IUserInfo } from '@nimbus-library/features/menu/models/i-user.info';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';

@Injectable({ providedIn: 'root' })
export class MsalAuthenticationService extends IAuthenticationService {
  public get isAuthenticated(): boolean {
    return this.isAuthenticated$.value;
  }

  private readonly authority: string;

  private readonly idTokenRefreshThresholdInMinutes = 50;

  private readonly loginCompleted$: Subject<AuthenticationResult> = new Subject();

  public status$: Observable<InteractionStatus> = this.msalBroadcastService.inProgress$;

  public constructor(
    private msalBroadcastService: MsalBroadcastService,
    private readonly msalService: MsalService,
    private readonly router: Router,
    @Optional() private readonly teamsContextAccessor: IMicrosoftTeamsContextAccessor,
    recurringTaskRunnerFactory: RecurringTaskRunnerFactory,
    authenticationStorage: IAuthenticationStorage,
  ) {
    super(recurringTaskRunnerFactory, authenticationStorage);
    this.authority = `https://login.microsoftonline.com/${this.environmentConfiguration.authority || 'common'}`;
    this.msalService.instance.enableAccountStorageEvents();
    this.handleRedirectObservable().subscribe();
    this.trackAuthentication().subscribe();
  }

  public trackAuthentication(): Observable<void> {
    return this.msalBroadcastService.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      tap((result: EventMessage) => {
        if (
          result.eventType === EventType.ACCOUNT_REMOVED &&
          !!this.msalService.instance.getActiveAccount() &&
          this.msalService.instance.getActiveAccount().username === (result.payload as AccountInfo).username
        ) {
          this.logout(false, {
            autoLogin: false,
            returnUrl: this.getReturnUrl(),
            prompt: Prompt.SelectAccount,
          });
        } else if (result.eventType === EventType.ACCOUNT_ADDED) {
          this.msalService.instance.setActiveAccount(result.payload as AccountInfo);
          const userinfo = this.getUserInfo();
          this.teamsContextAccessor?.trySpecifyUser({
            loginHint: userinfo.userName,
            tenantId: userinfo.tenantId,
            userId: userinfo.userId,
          });
          this.isAuthenticated$.next(true);

          // Update UI to show user is signed in. result.payload contains the account that was logged in
        }
      }),
      mapTo(undefined),
    );
  }

  public handleRedirectObservable() {
    return this.msalService.handleRedirectObservable().pipe(
      tap((result) => {
        this.handleLogin(result);
      }),
    );
  }

  public handleLogin(result: AuthenticationResult) {
    if (result) {
      if (result.state === 'consent') {
        this.getApiToken$(true).pipe(take(1)).subscribe();
      }

      this.msalService.instance.setActiveAccount(result.account);
      const userInfo = this.getUserInfo();
      this.authenticationStorage.saveAuthData(result.idToken, userInfo, moment(result.expiresOn));
      this.teamsContextAccessor?.trySpecifyUser({
        loginHint: userInfo.userName,
        tenantId: userInfo.tenantId,
        userId: userInfo.userId,
      });

      this.isAuthenticated$.next(true);
    }

    this.loginCompleted$.next(result);
  }
  // IMPORTANT: It MUSTN'T be called with empty scope because it would go to the backend instead of getting the token from the cache
  public getApiToken$(forceRefresh = false, scopes: string[] = ['user.read']): Observable<IApiToken> {
    const account = this.syncAccount();

    if (!!account) {
      return this.msalService
        .acquireTokenSilent({
          account,
          scopes,
          forceRefresh,
          authority: this.authority,
        })
        .pipe(map((authResult: AuthenticationResult) => this.processToken(authResult.accessToken, MicrosoftResource.GraphApi)));
    } else {
      this.logout(false, { autoLogin: false, returnUrl: this.getReturnUrl() }, true);
    }
  }
  // IMPORTANT: It MUSTN'T be called with empty scope because it would go to the backend instead of getting the token from the cache
  public getRawApiToken$(forceRefresh = false, scopes: string[] = ['user.read']): Observable<string> {
    const account = this.syncAccount();

    if (!!account) {
      return this.msalService
        .acquireTokenSilent({
          account,
          scopes,
          forceRefresh,
          authority: this.authority,
        })
        .pipe(map((authResult: AuthenticationResult) => authResult.accessToken));
    } else {
      this.logout(false, { autoLogin: false, returnUrl: this.getReturnUrl() }, true);
    }
  }

  public grantExplicitConsent$(scopes: string[], redirectUrl: string): Observable<AuthenticationResult | string> {
    return this.teamsContextAccessor
      ? this.teamsContextAccessor.getClientInfo$().pipe(
          mergeMap(() => {
            if (this.runningInTeams()) {
              return this.grantExplicitTeams(scopes);
            }

            return this.grantExplicitBrowser(scopes);
          }),
        )
      : this.grantExplicitBrowser(scopes).pipe(mapTo(undefined));
  }

  public getUserInfo(): IUserInfo {
    const account = this.msalService.instance.getActiveAccount() || first(this.msalService.instance.getAllAccounts());
    return account
      ? {
          name: account.name,
          userName: account.username,
          tenantId: account.tenantId,
          userId: account.homeAccountId.split('.')[0],
        }
      : {
          name: null,
          userName: null,
          tenantId: null,
          userId: null,
        };
  }

  public grantAdminConsent(scopes: string[], redirectUrl: string) {
    window.open(this.createAdminConsentLink(scopes, redirectUrl), '_blank');
  }

  public login() {
    const teamsInfo = this.authenticationStorage?.getTeamsInfo();
    if (teamsInfo) {
      return this.sso(teamsInfo).pipe(
        catchError(() => this.interactiveLogin()),
        switchMap((res) => {
          if (!res) {
            return this.interactiveLogin();
          }

          return of(res);
        }),
      );
    }

    return this.interactiveLogin();
  }

  private interactiveLogin() {
    this.authenticationStorage.saveTeamsInfo(null, moment().add(1, 'year'));
    return this.teamsContextAccessor
      ? this.teamsContextAccessor.getClientInfo$().pipe(
          mergeMap(() => {
            if (this.runningInTeams()) {
              return this.loginTeams();
            }

            return this.loginPopup();
          }),
        )
      : this.loginPopup().pipe(mapTo(undefined));
  }

  public sso(loginHint: string) {
    return this.msalService
      .ssoSilent({
        loginHint,
        redirectUri: '/html/authentication/end.html',
      })
      .pipe(
        tap((result) => this.handleLogin(result)),
        map(() => this.isAuthenticated$.value),
      );
  }

  public loginRedirect(customCallback?: string, scopes?: string[]) {
    const defaultScopes = ['user.read'];
    this.msalService.loginRedirect({
      prompt: 'select_account',
      scopes: scopes ? [...scopes, ...defaultScopes] : defaultScopes,
      redirectUri: customCallback,
      redirectStartPage: customCallback,
    });
  }

  public loginPopup() {
    return this.msalService
      .loginPopup({
        prompt: 'select_account',
        scopes: ['user.read'],
        redirectUri: '/html/authentication/end.html',
      })
      .pipe(
        tap((result) => {
          this.handleLogin(result);
        }),
      );
  }

  public loginTeams() {
    return from(
      authentication.authenticate({
        url: `${window.location.origin}/authentication/teams/start`,
      }),
    ).pipe(
      catchError((err) => {
        console.error(err);
        return throwError(err);
      }),
      mergeMap(() =>
        this.msalService.acquireTokenSilent({
          account: this.msalService.instance.getActiveAccount(),
          authority: this.authority,
          scopes: ['user.read'],
        }),
      ),
      tap((result) => this.handleLogin(result)),
    );
  }

  public grantExplicitTeams(scopes: string[]) {
    return from(
      authentication.authenticate({
        url: `${window.location.origin}/authentication/teams/start?scopes=${encodeURI(scopes.join(','))}&explicitConsent=true`,
        isExternal: false,
      }),
    ).pipe(
      catchError((err) => {
        console.error(err);
        return throwError(err);
      }),
    );
  }

  public grantExplicitBrowser(scopes: string[]) {
    return this.msalService.acquireTokenPopup({
      scopes,
      redirectUri: '/html/authentication/end.html',
      state: 'consent',
      prompt: 'consent',
      authority: this.authority,
    });
  }

  public grantExplicitRedirect(redirectUrl: string, scopes: string[]) {
    return this.msalService.acquireTokenRedirect({
      scopes,
      redirectUri: redirectUrl,
      redirectStartPage: redirectUrl,
      state: 'consent',
      prompt: 'consent',
      authority: this.authority,
    });
  }

  public aquireIdToken$(loginHint?: string): Observable<{ token: string; expiration: Moment }> {
    this.msalService.getLogger().verbose('MSAL aquireIdToken$ activated');

    const account = this.syncAccount(loginHint);

    let forceRefresh = false;
    if (!!account) {
      const idTokenClaims: { exp?: number } = account.idTokenClaims;

      if (!idTokenClaims?.exp) {
        this.logout(false, { autoLogin: false, returnUrl: this.getReturnUrl() });
        return EMPTY;
      }

      forceRefresh = moment(idTokenClaims.exp * 1000).diff(moment(), 'minutes', true) <= this.idTokenRefreshThresholdInMinutes;
    } else {
      return throwError('Account not defined'); // no account found, it's not possible to aquireIdToken
    }

    const authRequest: SilentRequest = {
      scopes: this.defaultScopes,
      account,
      correlationId: undefined,
      forceRefresh,
      authority: `https://login.microsoftonline.com/${this.environmentConfiguration.authority || 'common'}`,
    };

    // Note: For MSA accounts, include openid scope when calling acquireTokenSilent to return idToken
    return this.msalService.acquireTokenSilent({ ...authRequest }).pipe(
      catchError((err) => {
        this.msalService.getLogger().error('aquireIdToken$ - acquireTokenSilent rejected with error. Invoking interaction to resolve.');
        this.logout(false, { autoLogin: false, returnUrl: this.getReturnUrl() });
        return EMPTY;
      }),
      switchMap((result: AuthenticationResult) => {
        if (!result.accessToken) {
          this.msalService
            .getLogger()
            .error(
              'aquireIdToken$ - acquireTokenSilent resolved with null access token. Known issue with B2C tenants, invoking interaction to resolve.',
            );
          this.logout(false, { autoLogin: false, returnUrl: this.getReturnUrl() });
          return EMPTY;
        }
        return of(result);
      }),
      tap(() => {
        const userinfo = this.getUserInfo();
        this.teamsContextAccessor?.trySpecifyUser({
          loginHint: userinfo.userName,
          tenantId: userinfo.tenantId,
          userId: userinfo.userId,
        });
      }),
      map((result: AuthenticationResult) => ({ token: result.idToken, expiration: moment(result.expiresOn) })),
    );
  }

  private syncAccount(loginHint?: string): AccountInfo | null {
    let account: AccountInfo;
    if (loginHint) {
      account = this.msalService.instance.getAccountByUsername(loginHint);
    }

    if (!account) {
      if (!!this.msalService.instance.getActiveAccount()) {
        this.msalService.getLogger().verbose('aquireIdToken$ - active account selected');
        account = this.msalService.instance.getActiveAccount();
      } else {
        this.msalService.getLogger().verbose('aquireIdToken$ - no active account, fallback to first account');

        account =
          this.msalService.instance.getAccountByUsername(this.authenticationStorage.getUserInfo()?.userName) ||
          this.msalService.instance.getAllAccounts()[0];
      }
    }

    if (!!account) {
      this.msalService.instance.setActiveAccount(account);
    }

    return account;
  }
  private acquireTokenInteractively(authRequest: MsalInterceptorAuthRequest, scopes: string[]): Observable<AuthenticationResult> {
    this.msalService.getLogger().verbose('aquireIdToken$ - error acquiring token silently, acquiring by popup');
    this.msalService.acquireTokenPopup({ ...authRequest, scopes });
    return EMPTY;
  }

  private cleanUpLocalMsalAuthenticationInfo() {
    const localStorageIndexes = localStorage.length;
    const msalKeys = [];

    for (let i = 0; i < localStorageIndexes; i++) {
      const key = localStorage.key(i);

      if (!key.startsWith('Nimbus')) {
        msalKeys.push(key);
      }
    }

    msalKeys.forEach((key) => localStorage.removeItem(key));
  }

  public logout(destroyServerSession: boolean, logoutRequest: LogoutRequest, reload: boolean = true) {
    const account = this.msalService.instance.getAccountByUsername(this.authenticationStorage.getUserInfo()?.userName);
    this.isAuthenticated$.next(false);
    this.authenticationStorage.clearAuthData();
    this.logout$.next();

    if (destroyServerSession) {
      this.msalService.logoutRedirect({
        account,
        postLogoutRedirectUri: logoutRequest.returnUrl
          ? `/authentication/login?autoLogin=false&returnUrl=${encodeURIComponent(logoutRequest.returnUrl)}`
          : '/authentication/login?autoLogin=false',
      });
    } else {
      this.cleanUpLocalMsalAuthenticationInfo();
      this.router
        .navigate(['/', 'authentication', 'login'], {
          queryParams: logoutRequest,
          queryParamsHandling: 'merge',
        })
        .then(() => {
          if (reload) {
            window.location.reload();
          }
        });
    }
  }
}
